import React from 'react';
import { maximumOf } from 'components/utils/minMax';

// Shared rendering helpers for a manufacturing-line listing's cards, so the main listing
// (ManufacturingLineLists) and the standard-lines fallback shown when the owner's own lines come up
// empty (StandardManufacturingLinesFallback) draw a card the SAME way -- one source of truth for the
// stars, the column de-duplication, the sub_section badges and the common machine scale.

// The star rating for a score: the BEST (lowest, cheapest) score is always 5 stars, and every other score is
// PROPORTIONAL to it -- a plan that costs twice the best earns half the stars (best/score * 5). Not a position
// in the on-screen range: a plan only a little dearer than the best now reads a little under 5, rather than
// being stretched down to 1 just for being the worst on screen. The same shape the solution listing uses
// (sortSolutionsByScoreAndDrawStars). null for a plan with no score; 5 when this IS the best (or the only one).
export const starRatingForScore = (score, bestScore) => {
  if (score == null) { return null; }
  if (!(bestScore > 0) || !(score > 0)) { return 5; }

  return Math.round(Math.max(1, Math.min(5, (bestScore / score) * 5)) * 10) / 10; // best = 5, never below 1
};

// A star row for a rating -- full stars, a half star, empty stars, then the numeric rating. The same rendering
// the legacy solution listing draws, in React.
export const Stars = ({ rating }) => {
  if (rating == null) { return null; }

  const full = Math.floor(rating);
  const remainder = rating - full;
  const hasHalf = remainder >= 0.25 && remainder < 0.75;
  const empty = Math.max(0, 5 - full - (hasHalf ? 1 : 0));

  return (
    <span style={{ color: '#ffc107', fontSize: '14px' }}>
      {'★'.repeat(full)}
      {hasHalf ? (
        <span style={{ position: 'relative', display: 'inline-block' }}>
          <span style={{ color: '#ddd' }}>★</span>
          <span style={{ position: 'absolute', left: 0, width: '50%', overflow: 'hidden', color: '#ffc107' }}>★</span>
        </span>
      ) : null}
      {'☆'.repeat(empty)}
      <span style={{ marginLeft: '4px', color: '#666', fontSize: '13px' }}>{rating.toFixed(1)}</span>
    </span>
  );
};

// Which columns each manufacturing line in a list draws, so a machine two of the list's lines share is drawn
// once and the repeat's column is left empty rather than an identical machine pair drawn again. A line whose
// every machine is already on screen keeps no card of its own (drawsNothing) -- its part is not lost, it rides
// on the badge of each machine it shares (partLabelsByMachineId), wherever that machine is shown.
//
// machineId -> drawn, once some earlier line has drawn it. FIRST drawer wins: that is the row the machine is
// actually drawn on.
export const columnVisibilityFor = (manufacturingLines) => {
  const drawn = new Set();

  return manufacturingLines.map((manufacturingLine) => {
    const machineIds = [
      manufacturingLine.printing_machine_id,
      manufacturingLine.die_cutting_machine_id,
      manufacturingLine.printing_and_die_cutting_machine_id
    ];
    const alreadyDrawn = (machineId) => machineId != null && drawn.has(machineId);
    const ownMachineIds = machineIds.filter((machineId) => machineId != null);

    const visibility = {
      hidePrintingColumn: alreadyDrawn(manufacturingLine.printing_machine_id),
      hideDieCuttingColumn: alreadyDrawn(manufacturingLine.die_cutting_machine_id),
      hideCombinedColumn: alreadyDrawn(manufacturingLine.printing_and_die_cutting_machine_id),
      // Every machine this line would draw is already on screen, so it has no box left of its own -- drop the
      // empty card. Its part still surfaces, on the badges of the shared machines (partLabelsByMachineId).
      drawsNothing: ownMachineIds.length > 0 && ownMachineIds.every(alreadyDrawn)
    };

    machineIds.forEach((machineId) => {
      if (machineId != null) { drawn.add(machineId); }
    });
    return visibility;
  });
};

// The parts each machine of a list makes, keyed by machine id -- the badge belongs to the machine, not to a
// group heading above it. A machine two of the list's parts share (one die-cutter used by both Cover and Tray)
// maps to BOTH labels, so a shared machine, drawn once, shows every part it makes; a line dropped for drawing
// nothing (columnVisibilityFor) still surfaces here, on the machines it shares. Only sub_sections that carry a
// label (multi-part sections) contribute -- a single-part section passes a blank label and its machines carry
// no badge.
export const partLabelsByMachineId = (list) => {
  const labelsByMachineId = new Map();

  list.manufacturing_lines.forEach((manufacturingLine) => {
    const label = manufacturingLine.sub_section_label;
    if (!label) { return; }

    [
      manufacturingLine.printing_machine_id,
      manufacturingLine.die_cutting_machine_id,
      manufacturingLine.printing_and_die_cutting_machine_id
    ].forEach((machineId) => {
      if (machineId == null) { return; }

      const labels = labelsByMachineId.get(machineId) || [];
      if (!labels.includes(label)) {
        labels.push(label);
        labelsByMachineId.set(machineId, labels);
      }
    });
  });

  return labelsByMachineId;
};

// The largest machine sheet dimension across every manufacturing line in the given lists -- the common scale
// that makes the per-machine rectangles comparable to each other (a bigger machine renders bigger), including
// across two lines stacked in one card. The same scale the legacy listing computes. At least 1, so an empty set
// never divides the card renderer by zero.
export const maxMachineDimensionOf = (lists) => {
  const allManufacturingLines = lists.flatMap((list) => list.manufacturing_lines);
  return Math.max(1, maximumOf(allManufacturingLines.flatMap((manufacturingLine) => [
    manufacturingLine.sheet_width_mm, manufacturingLine.sheet_height_mm,
    manufacturingLine.printing_sheet_width_mm, manufacturingLine.printing_sheet_height_mm
  ].map((value) => parseFloat(value) || 0))));
};
